import type { PostMediaItem } from '@ps/api/types';

import { PostImageMedia } from './PostImageMedia';
import { PostLiveReplayMedia } from './PostLiveReplayMedia';
import { PostVideoMedia } from './PostVideoMedia';

interface PostMediaProps {
  media: PostMediaItem;
  /**
   * Whether this slide is the currently visible one — drives video auto-pause:
   * an off-screen video (`active=false`) pauses and resumes from position on
   * re-entry. Mirrors legacy `MediaObjectView` (`slide-view.tsx:389-411`).
   */
  active: boolean;
  /** Host page focus — a blurred / hidden tab also pauses video. */
  focused?: boolean;
  /** alt / a11y label. Falls back to `media.caption`. */
  alt?: string;
  /** Class name applied to the media element. */
  className?: string;
  /** Native video controls, on by default — see `PostVideoMedia`. */
  controls?: boolean;
}

/**
 * Leaf renderer for a single `PostMediaItem` — dispatches on `media.type` to the
 * IMAGE / VIDEO / LIVE_REPLAY branch, each its own component. 1:1 with legacy
 * `MediaObjectView` in `flows/bio/wall/components/post/slide-view.tsx:424-471`.
 */
export function PostMedia({
  media,
  active,
  focused = true,
  alt,
  className,
  controls = true,
}: PostMediaProps) {
  if (media.type === 'IMAGE') {
    return <PostImageMedia media={media} alt={alt} className={className} />;
  }
  if (media.type === 'LIVE_REPLAY') {
    return (
      <PostLiveReplayMedia media={media} alt={alt} className={className} />
    );
  }
  // VIDEO + any unrecognized future type (legacy default switch case).
  return (
    <PostVideoMedia
      media={media}
      active={active}
      focused={focused}
      alt={alt}
      className={className}
      controls={controls}
    />
  );
}
